import React, { useEffect, useState } from 'react'
import { DataGrid, NGODatePicker } from '../../../common/index.jsx';
import usePermissions from '../../../utils/userPermissions.js';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {gridRowStyles} from '../../../utils/configs.js';
import { APIEndpoint } from '../../../utils/axios.js';
import downloadMasterIcon from "../../../assets/icons/download-master.svg";
import closeIcon from '../../../images/icons/Icon2.png';
import LoadingGIF from '../../../assets/loading.gif';
import { saveAs } from 'file-saver';
import masterEventCodes from './masterEventCodes.js';
import { MdVisibility, MdContentCopy } from "react-icons/md";
import useSidebarHidden from '../../../common/useSidebarHidden.jsx';

const allEventCodes = masterEventCodes.getAllEventCodes() || {};
const eventCodeMap = allEventCodes.reduce((acc, item) => {
    acc[item.code] = item;
    return acc;
}, {});

const allEventCategories = masterEventCodes.getEventCategories() || [];
const allEventCategoriesMap = allEventCategories.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
}, {});

const DATETIME_FORMAT = "DD-MM-YYYY hh:mm A";
const MAX_DATE_RANGE_DAYS = 5;
const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again after some time.";

let axiosAbortCtrl = [];
// axiosAbortCtrl["orderListAPI"] = new AbortController();

const newAbortSignal = (apiName, timeoutMs = 5000) => {

  axiosAbortCtrl[apiName] = new AbortController();
  // setTimeout(() => axiosAbortCtrl[apiName].abort(), timeoutMs || 0);

  return axiosAbortCtrl[apiName].signal;
};
const abortEarlierRequest = (apiName = "") => {
    if (apiName === "" || typeof axiosAbortCtrl[apiName] === "undefined") return;

    axiosAbortCtrl[apiName].abort(
        apiName + ": Cancelling API running earlier..."
    );
};

const decodeLogMessage = (message = '', replaceWith = '') => {
    try {
        let decodedMessage = decodeURI(decodeURIComponent(message));

        /** Replace escaped commas with normal commas */
        try {
            decodedMessage = decodedMessage.replace(/\\,/g, ',');
        } catch (error) {
            console.error('Log: Error replacing escaped commas:', error);
        }
        
        /** Format JSON message if applicable */
        if (decodedMessage.startsWith('{') && decodedMessage.endsWith('}')) {
            try {
                decodedMessage = JSON.stringify(JSON.parse(decodedMessage), null, 2);
            } catch (error) {
                console.error('Log: Error parsing decoded message as JSON:', error);
            }
        }

        if (replaceWith) {
            const words = decodedMessage.split(/:\|:|\|/).filter(Boolean);
            decodedMessage = words.map(word => word.trim()).join(replaceWith);
        }

        return decodedMessage;
    } catch (_error) {
        return message;
    }
};

const getStatusClassName = (statusCode) => {
    const code = Number(statusCode);

    if (code >= 200 && code < 300) return 'http-status-code status-success';
    if (code >= 300 && code < 400) return 'http-status-code status-redirect';
    if (code >= 400 && code < 500) return 'http-status-code status-client-error';
    if (code >= 500 && code < 600) return 'http-status-code status-server-error';

    return 'http-status-code';
};

const renderMessageWithHighlights = (params, joinWith = '') => {
    const message = params.data?.message || '';
    const decodedMessage = decodeLogMessage(message);
    if (!decodedMessage) return '';

    const parts = [];

    if (params.data?.eventCode && params.data.eventCode === 'EVENT_70009') {
        const url = decodedMessage.trim();
        const isSafeUrl = /^https?:\/\//i.test(url);
        parts.push(
            isSafeUrl ? (
                <a
                    key={`url-${url}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="log-message-url"
                >
                    {url}
                </a>
            ) : (
                <span key={`url-${url}`} className="log-message-url">{url}</span>
            )
        );
    }
    else if (params.data?.eventCode && params.data.eventCode === 'EVENT_70010') {
        const httpText = decodedMessage.split(':|:');
        if (httpText.length >= 2) {
            const url = httpText[0].trim();
            const httpStatusCode = httpText[1].trim();

            parts.push(
                <a
                    key={`url-${url}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="log-message-url"
                >
                    {url}
                </a>,
                <div key={`status-${httpStatusCode}`} className={getStatusClassName(httpStatusCode)}>{httpStatusCode}</div>
            );
        }
    }
    else {
        let lineParts = [];
        
        const words = decodedMessage.split(/:\|:|\|/).filter(Boolean);
        words.forEach((word, index) => {
            const originalWord = word.trim();
            const trimmedWord = originalWord.toLocaleLowerCase();

            if (trimmedWord.startsWith('seatno:') || trimmedWord.startsWith('pmttype:')) {
                lineParts.push(<span key={`word-${index}`} className='log-message-word log-message-seatno'>{originalWord}</span>);
            }
            else if (trimmedWord.startsWith('saletype:')) {
                lineParts.push(<span key={`word-${index}`} className='log-message-word log-message-saletype'>{originalWord}</span>);
            }
            else if (trimmedWord.startsWith('items:')) {
                const isJson = (str) => {
                    try {
                        return JSON.parse(str);
                    } catch (e) {
                        return false;
                    }
                };
                const itemsValue = originalWord.split(':', 2)[1];
                const jsonData = isJson(itemsValue.replaceAll('|', ','));
                if (jsonData) {
                    lineParts.push(<span key={`word-${index}`} className='log-message-word log-message-items'>{JSON.stringify(jsonData)}</span>);
                }
            }
            else if (/^[a-zA-Z]+:\d+(\.\d+)?$/.test(trimmedWord)) {
                lineParts.push(<span key={`word-${index}`} className='log-message-word log-message-price'>{originalWord}</span>);
            }
            else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} [AP]M$/.test(trimmedWord) && moment(trimmedWord).isValid()) {
                lineParts.push(<span key={`word-${index}`} className='log-message-word log-message-date'>{moment(originalWord).format('YYYY-MM-DD HH:mm:ss A')}</span>);
            }
            else {
                lineParts.push( <span key={`word-${index}`} className='log-message-word'>{originalWord}</span> );
            }
        });
        parts.push(...lineParts);
    }

    if (joinWith) {
        return parts.reduce((acc, part, index) => {
            if (index > 0) {
                acc.push(<span key={`separator-${index}`} className='log-message-separator'>{joinWith}</span>);
            }
            acc.push(part);
            return acc;
        }, []);
    }

    return parts;
};

const logsMessageCellRenderer = (params) => {
    return (
        <div className="logs-message-cell">
            {params.data && params.data?.message ? renderMessageWithHighlights(params) : ''}
        </div>
    );
};

const eventCodeCellRenderer = (params) => {
    const eventCategory = masterEventCodes.getEventCategoryByEventCode(params.data?.eventCode);
    
    if (params.data && params.data?.eventCode) {       
        return <span className={`log-eventcode-cell ${eventCategory?.cssClass ? eventCategory.cssClass : ''}`}>{ params.data?.eventCode }</span>;
    }
    else {
        return <span>{params.data?.eventCode || ''}</span>;
    }
};
const eventCodeTitleRenderer = (params) => {   
    if (params.data && params.data?.eventCode) {
        let prntStr = ''
        if (eventCodeMap[params.data?.eventCode]) {
            prntStr = eventCodeMap[params.data?.eventCode]?.title ? eventCodeMap[params.data?.eventCode].title : params.data?.eventCode;
        } else {
            prntStr = params.data?.eventCode;
        }
        return <span className={`log-eventcode-cell`}>{prntStr}</span>;
    }
    else {
        return <span>{params.data?.eventCode || ''}</span>;
    }
};



export const DeviceLogsInnerPage = () => {
    const { t, ready } = useTranslation();
    const { hasPermission: viewDeviceLogs } = usePermissions('view', "portal.menu.logs");
    const { hasPermission: downloadDeviceLogs } = usePermissions('download', "portal.menu.logs");

    const [perPageLimit,] = useState(50);
    const [noResultsMessage, setNoResultsMessage] = useState('No Data');

    const [operationDate, setOperationDate] = useState("");
    const [deviceId, setDeviceId] = useState("");
    const [searchKey, setSearchKey] = useState("");
    
    const [isReloadTable, setIsReloadTable] = useState(false);
    const [makeAPIRequest, setMakeAPIRequest] = useState(false);
    const [apiData, setAPIData] = useState(null);
    const [state, setState] = useState({ isDownloadReportError: false, message: null });

    const [showDownload, setShowDownload]=useState(false);
    const [reportData, setReportData]=useState({ operationDate: "", deviceId: "", startDate: "", endDate: "" });
    const [isDownloading, setIsDownloading] = useState(false);

    const [selectedEventCategory, setSelectedEventCategory] = useState('');
    const [apiCategoryCount, setAPICategoryCount] = useState({});
    const [apiEventCount, setAPIEventCount] = useState({});
    const [apiCategoryEventsLink, setAPICategoryEventsLink] = useState({});
    
    const [selectedEventCode, setSelectedEventCode] = useState('');

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsData, setDetailsData] = useState(null);
    const sideBarHidden = useSidebarHidden('.content-container');

    useEffect(() => {
        if (apiData && apiData.items && apiData.items.length > 0) {
            /** Search and filter response based on searchKey */
            let srhFilteredResp = '';
            if (searchKey) {
                srhFilteredResp = apiData.items.filter(item => 
                    item.eventCode.toLowerCase().includes(searchKey.toLowerCase()) || 
                    (item.message && item.message.toLowerCase().includes(searchKey.toLowerCase())));
                apiData.items = srhFilteredResp;
            }
            
            /** Event codes count */
            const eventCodesCount = {};
            apiData.items.forEach((item) => {
                eventCodesCount[item.eventCode] = (eventCodesCount[item.eventCode] || 0) + 1;
            });
            setAPIEventCount(eventCodesCount);


            /** Category count and event codes */
            const categoryEventCodes = {};
            const apiCategoryCount = {};
            Object.keys(eventCodesCount).forEach((eventCode) => {
                const category = masterEventCodes.getEventCategoryByEventCode(eventCode);
                let key;
                if (category) {
                    key = category.key;
                    apiCategoryCount[key] = (apiCategoryCount[key] || 0) + eventCodesCount[eventCode];
                } else {
                    key = 'othersCodes';
                    apiCategoryCount[key] = (apiCategoryCount[key] || 0) + eventCodesCount[eventCode];
                }

                if (!categoryEventCodes[key]) {
                    categoryEventCodes[key] = [];
                }
                categoryEventCodes[key].push({code: eventCode, count: eventCodesCount[eventCode], title: eventCodeMap[eventCode]?.title || ''});
            });
            
            setAPICategoryCount(apiCategoryCount);
            setAPICategoryEventsLink(categoryEventCodes);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, apiData]);

    useEffect(() => {
        setAPICategoryCount({});
        setAPICategoryEventsLink({});
        setSelectedEventCategory('');
        setSelectedEventCode('');
        setAPIEventCount({});
        
        setMakeAPIRequest(true);
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ operationDate ]);
    useEffect(() => {
        setAPICategoryCount({});
        setAPICategoryEventsLink({});
        setSelectedEventCategory('');
        setSelectedEventCode('');
        setAPIEventCount({});
        
        setMakeAPIRequest(true);
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ deviceId ]);

    useEffect(() => {
        setMakeAPIRequest(false);
        setIsReloadTable(prev => !prev);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventCategory]);

    useEffect(() => {
        setMakeAPIRequest(false);
        setIsReloadTable(prev => !prev);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventCode]);

    useEffect(() => {
        setMakeAPIRequest(true);
        setIsReloadTable(prev => !prev);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchKey]);

    const Columns = [
        {field: "timestamp", headerName: "Timestamp",flex:1,maxWidth:150,cellStyle:gridRowStyles,resizable:true,valueGetter: params => {
            return params.data && params.data?.timestamp ? moment(params.data?.timestamp).format(DATETIME_FORMAT) : '';
        }},
        {field: "eventCode", headerName: "Event Code",flex:1,maxWidth:120,cellStyle:gridRowStyles,resizable:true,
            cellRenderer: eventCodeCellRenderer,
            cellClass: 'long-text-cell'
        },
        {field: "eventCode", headerName: "Event",flex:1,maxWidth:210,cellStyle:gridRowStyles,resizable:true, 
            cellRenderer: eventCodeTitleRenderer,
            cellClass: 'long-text-cell', tooltipValueGetter: params => {
                return params.data && params.data?.eventCode ? eventCodeMap[params.data?.eventCode]?.title || params.data?.eventCode : '';
            }
        },
        {field: "message", headerName: "Message",flex:1, minWidth:150, cellStyle: gridRowStyles, resizable:true, supressSizeToFit:true, cellClass: 'long-text-cell',
            cellRenderer: logsMessageCellRenderer,
            tooltipValueGetter: params => {
                return params.data && params.data?.message ? decodeLogMessage(params.data?.message) : '';
            }
        },
        { field: 'actions', pinned:'right', autoResize:true, headerName:"", width:80, cellRenderer: ActionComponent },
    ];

    function ActionComponent (props) {
        const {data} = props;
        if(!data) return null;

        return (
            <>
                <span className='viewDetails' title="View Details">
                    <MdVisibility alt="View Details" onClick={() => handleOpenDetailsModal(data)} />
                </span> &nbsp;&nbsp;
                <span className='copy p-2 m-2 pointer' title="Copy">
                    <MdContentCopy alt="Copy" onClick={(e) => handleCopyLogMessage(e.currentTarget, decodeLogMessage(data?.message, '\n'))} />
                </span>
            </>
        );
    }
    const handleOpenDetailsModal = (data) => {
        setShowDetailsModal(true);
        setDetailsData(data);
    };

    const handleCopyLogMessage = (context, data) => {
        if (data) {
            navigator.clipboard.writeText(data)
            .then(() => {
                context.style.color = '#4CAF50'; // Change icon color to green
            })
            .catch((error) => {
                context.style.color = '#f44336'; // Change color to red
                console.error('Failed to copy log message to clipboard:', error);
            });
        }
        else {
            context.style.color = '#f44336'; // Change color to red
        }
        
        setTimeout(() => {
            context.style.color = ''; // Reset text color
        }, 2000); // Reset after 2 seconds
    
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setDetailsData(null);
    };

    const fetchDeviceLogs = async (page = 1, perPage = 50) => {
        if(!viewDeviceLogs) {
            setAPIData({ items: [], count: 0 });
            setNoResultsMessage(t('invalidDate'));
            setAPICategoryCount({});
            setAPICategoryEventsLink({});
            setSelectedEventCategory('');
            setSelectedEventCode('');
            setAPIEventCount({});
            return { items: [], count: 0 }
        }

        let encodedDate = '';
        let encodedDeviceId = '';
        if(operationDate) {
            if (!isValidDate(operationDate)) {
                setAPIData({ items: [], count: 0 });
                setNoResultsMessage(t('invalidDate'));
                setAPICategoryCount({});
                setAPICategoryEventsLink({});
                setSelectedEventCategory('');
                setSelectedEventCode('');
                setAPIEventCount({});
                return { items: [], count: 0 }
            }
            encodedDate = encodeURIComponent(moment(operationDate).format('YYYY-MM-DD'));
        }
        if(deviceId) {
            encodedDeviceId = encodeURIComponent(deviceId.trim());
        }
        
        if ( !encodedDate || !encodedDeviceId ) {
            setAPIData({ items: [], count: 0 });
            setNoResultsMessage("Select both Operation Date and Device ID");
            setAPICategoryCount({});
            setAPICategoryEventsLink({});
            setSelectedEventCategory('');
            setSelectedEventCode('');
            setAPIEventCount({});
            return { items: [], count: 0 }
        }

        try {
        
            let endpoint = `device/${encodedDeviceId}/logs?operationDate=${encodedDate}`;

            // if(selectedEventCategory) {
            //     const eventCodesByCategory = masterEventCodes.getEventCodesByCategory(selectedEventCategory);
            //     if (!eventCodesByCategory || Object.keys(eventCodesByCategory).length === 0) {
            //         setNoResultsMessage('No Data for selected event category');
            //         return { items: [], count: 0 }
            //     }
            //     const eventCodesQueryParam = eventCodesByCategory.map(ec => ec.code).join(',');
            //     endpoint += `&eventCodes=${eventCodesQueryParam}`;
            // }
            
            let responseJSON = null;
            if ( makeAPIRequest ) {
                responseJSON = await APIEndpoint.get(`${endpoint}`, { Accept: 'application/json' }, { signal: newAbortSignal('deviceLogsAPI', 2000) });
                
                if(responseJSON && responseJSON.items && responseJSON.items.length > 0 && viewDeviceLogs) {
                    setMakeAPIRequest(false);
                    setAPIData(responseJSON);
                    
                    let endLimit = perPage * page;
                    /** Pagination */
                    if ( endLimit > responseJSON.items.length ) {
                        endLimit = responseJSON.items.length;
                    }
                    
                    const pgIndex = (page - 1) * perPage;
                    const pgLmtIndex = endLimit;
                    const paginatedApiData = { ...responseJSON, items: responseJSON.items.slice(pgIndex, pgLmtIndex) };
                    return paginatedApiData;
                } else if(!responseJSON?.items || responseJSON.items.length === 0){
                    setAPIData({ items: [], count: 0 });
                    setAPICategoryCount({});
                    setAPIEventCount({});
                    setAPICategoryEventsLink({});
                    setSelectedEventCategory('');
                    setSelectedEventCode('');
                    setNoResultsMessage('No Data');
                }
            }
            else {
                /** Filter by selected eventCode */
                const filteredITEMS = selectedEventCode ? 
                            apiData.items.filter(item => item.eventCode === selectedEventCode) : 
                            apiData.items;

                if (filteredITEMS.length === 0) {
                    setNoResultsMessage('No Data for selected event code');
                    return { items: [], count: 0 }
                }

                let endLimit = perPage * page;
                /** Pagination */
                if ( endLimit > apiData.items.length ) {
                    endLimit = apiData.items.length;
                }
                
                const pgIndex = (page - 1) * perPage;
                const pgLmtIndex = endLimit;
                const paginatedApiData = { ...apiData, items: filteredITEMS.slice(pgIndex, pgLmtIndex) };
                return paginatedApiData;
            }
        } catch (error) {
            console.log('Log: Error while fetching data', error);
        }
    };

    function isValidDate(dateString) {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    }
    
    const handleDateChange = (e) => {
        let { name, value } = e.target;
        if (!value) {
            setOperationDate('');
            return;
        }
        if (name === "operationDate") {
            setOperationDate(value);
        }
        abortEarlierRequest("deviceLogsAPI");
    };

    function onDeviceIdInpChange(e){
        let { name, value } = e.target;
        if(name === "deviceId") {
            setDeviceId(value);
        }
        abortEarlierRequest("deviceLogsAPI");
    }

    function onSearchKeyChange(e){
        let { name, value } = e.target;
        if(name === "searchKey") {
            setSearchKey(value);
        }
        abortEarlierRequest("deviceLogsAPI");
    }

    function onClickEventCategory(value) {
        setSelectedEventCategory(value);

        abortEarlierRequest("deviceLogsAPI");
    }

    function onClickEventCode(value) {
        setSelectedEventCode(value);

        abortEarlierRequest("deviceLogsAPI");
    }

    const onReportDateChange = (e) => {
        const { name, value } = e.target;
        setReportData((prev) => ({ ...prev, [name]: value }));
    }

    const getEndDateMaxDate = (startDate) => {
        if (!startDate) {
            return new Date();
        }
        const maxDate = new Date(startDate).getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000;
        const today = new Date().getTime();
        return maxDate > today ? new Date() : new Date(maxDate);
    };
    const onReportDeviceIdChange = (e) => {
        const { name, value } = e.target;
        setReportData((prev) => ({ ...prev, [name]: value }));
    }

    const handleCloseDownloadReport = () => {
        setShowDownload(false);
        setReportData({  });
        setState(prev => ({ ...prev, isDownloadReportError: false, message: { text: "" } }));
    }

    const onClickDownloadReport = async (e) => {
        handleCloseDownloadReport();
        setShowDownload(true);
    };

    const onDownloadDeviceLogsReport = async (e)=> {
        setState(prev => ({ ...prev, isDownloadReportError: false, message: { text: "" } }));

        const headers = {
            'Accept':'text/csv'
        };
        
        if (!reportData.startDate || !reportData.endDate) {
            setState((_) => {
                return { ..._, isDownloadReportError: true, 
                    message: {
                        text: "Please select both start date and end date."
                    }
                };
            });
            return;
        }

        try {
            let encodedStartDate = '',
                encodedEndDate = '',
                encodedDeviceId = '';
            if(reportData.startDate && reportData.endDate) {
                if (!isValidDate(reportData.startDate) || !isValidDate(reportData.endDate)) {
                    setNoResultsMessage(t('invalidDate'));
                    return { items: [], count: 0 }
                }
                encodedStartDate = encodeURIComponent(moment(reportData.startDate).format('YYYY-MM-DD'));
                encodedEndDate = encodeURIComponent(moment(reportData.endDate).format('YYYY-MM-DD'));
            }
            if(reportData.reportDeviceId) {
                encodedDeviceId = encodeURIComponent(reportData.reportDeviceId.trim());
            }
            
            if ( !encodedStartDate || !encodedEndDate || !encodedDeviceId ) {
                setNoResultsMessage(t('selectRequiredFields'));
                return { items: [], count: 0 }
            }
            
            setIsDownloading(true);
            const endpoint = `device/${encodedDeviceId}/logs?startDate=${encodedStartDate}&endDate=${encodedEndDate}`;
            const responseCSV = await APIEndpoint.get(endpoint, headers, { signal: newAbortSignal('deviceLogsAPI', 2000) });
                
            setIsDownloading(false);
            handleCloseDownloadReport();
            
            const safeStartDate = encodedStartDate.replace(/[^0-9-]+/g, '');
            const safeEndDate = encodedEndDate.replace(/[^0-9-]+/g, '');
            const safeDeviceId = encodedDeviceId.replace(/[^a-z0-9_-]+/gi, '_');
            
            const blob = new Blob([responseCSV], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `device-logs-${safeDeviceId || 'unknown'}-${safeStartDate}-${safeEndDate}.csv`);
        }
        catch (error) {
            setIsDownloading(false);
            console.error('Log: Error while downloading:', error);
            try {
                const errorTxt = JSON.parse( error?.message );
                setState((_) => {
                    return { ..._, isDownloadReportError: true,
                        message: {
                            text: error?.data?.message || errorTxt?.data?.message || DEFAULT_ERROR_MESSAGE
                        }
                    };
                });

            } catch (err) {
                console.error('Log: Error parsing error message:', err);
                setState((_) => {
                    return { ..._, isDownloadReportError: true, 
                        message: { 
                            text: err?.data?.message || DEFAULT_ERROR_MESSAGE
                        }
                    };
                });
            }
        }
    };

    return (
        <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
        { viewDeviceLogs ?
        <>
            <div
                className="flexRow flexWrap justifyContentFlexStart alignItemsCenter w-100 mb-1 deviceLogs-filter-container"
            >
                <div className='flexCol mx-1'>
                    <NGODatePicker
                        type="date"
                        name='operationDate'
                        value={operationDate}
                        onChange={(e) => handleDateChange(e)}
                        placeholder="Operation Date *"
                        isclearable={"true"}
                    />
                </div>

                <div className="flexCol">
                    <div className='operations-in mx-1'>
                        <input
                            name='deviceId'
                            className="search-crew searchReconFilter"
                            onChange={(e) => onDeviceIdInpChange(e)}
                            type="search"
                            placeholder="Search Device ID *"
                            value={deviceId || ''}
                        />
                    </div>
                </div>
                
                <div className="flexCol alignItemsRight justifyContentFlexEnd ">
                    <div className='px-2'>
                        { downloadDeviceLogs ? (
                                <>
                                    <button 
                                        className='dropdownStyle1-TextMedium'
                                        style={{marginLeft: "auto", marginRight: "0.5rem"}}
                                        disabled={isDownloading}
                                        onClick={ (e) => onClickDownloadReport(e)}
                                        title={"Download Device Logs"}>
                                            <img src={downloadMasterIcon} alt='Download' className='icon-size-min mt-1' title={"Download Device Logs"} />
                                    </button>
                                </>
                            ) : null
                        }
                    </div>
                </div>

                <div className="flexCol justifyContentFlexEnd alignItemsFlexEnd search-key-container">
                    <div className="operations-in ">
                        <input 
                            name='searchKey'
                            type="search"
                            placeholder="Search"
                            title="Search by Event Code or Message"
                            value={searchKey || ''}
                            className="search-key-inp search-crew searchReconFilter"
                            onChange={(e) => onSearchKeyChange(e)}
                        />

                    </div>
                </div>
            </div>

            {apiCategoryCount && Object.keys(apiCategoryCount).length > 0 ? (
            <>
                <div className="flexRow flexWrap justifyContentFlexStart alignItemsCenter sm-scrollbar">
                    <div className='flexCol'>
                        <ul className="event-category-list">
                            <li key={'allCategories'}
                                className={`event-category-item ${selectedEventCategory === '' ? 'selected' : ''} all-codes`}
                                onClick={() => onClickEventCategory('')}
                            >
                                All  <span className='itemsCount'>{Object.values(apiCategoryCount).reduce((acc, count) => acc + count, 0)}</span>
                            </li>
                            { Object.keys(apiCategoryCount).map((categoryKey, iCount) => (
                                <li
                                    key={categoryKey}
                                    className={`event-category-item 
                                        ${selectedEventCategory === categoryKey ? 'selected' : ''} 
                                        ${allEventCategories.find(cat => cat.key === categoryKey)?.cssClass || ''}`
                                    }
                                    onClick={() => onClickEventCategory(categoryKey)}
                                >
                                    {allEventCategories.find(cat => cat.key === categoryKey)?.label || categoryKey} <span className='itemsCount'>{apiCategoryCount[categoryKey] || 0}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </>
            ) : null}

            {apiCategoryEventsLink && Object.keys(apiCategoryEventsLink).length > 0 ?
                <>
                <div className="flexRow flexWrap justifyContentFlexStart alignItemsCenter sm-scrollbar category-eventCode-list-block">
                    <ul className="">
                        <li key={'allEventCodes'}
                            className={`category-eventCode-item ${selectedEventCode === '' ? 'selected' : ''}`} 
                            onClick={() => onClickEventCode('')}>
                            <span className='eventCode-label'>All</span>
                        </li>

                        {
                        (selectedEventCategory ? 
                            apiCategoryEventsLink[selectedEventCategory] : 
                            Object.values(apiCategoryEventsLink).flat()
                        )
                        .map((evnt, index) => (
                            <li
                                key={index}
                                onClick={() => onClickEventCode(evnt.code)}
                                className={`category-eventCode-item ${selectedEventCode === evnt.code ? 'selected' : ''} ${allEventCategoriesMap[selectedEventCategory]?.cssClass || ''}`}
                                title={eventCodeMap[evnt.code]?.title || evnt.code}
                            >
                                <span className='eventCode-label'>{evnt.code}</span> &nbsp;
                                <span className='itemsCount'>{evnt.count || 0}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                </>
                : null
            }

            <DataGrid
                className="full-flex"
                columns={[...Columns]}
                initialPageLimit={ perPageLimit }
                showDefaultFilters={false}
                getRows={ fetchDeviceLogs }
                columnResize={ true }
                rowSelection="multiple"
                resetDataSource={isReloadTable}
                noResultsMessage={noResultsMessage}
            />
        </>:
            <div className='flexCol justifyContentCenter alignItemsCenter width100 height100'>
                <div className='flexCol justifyContentCenter alignItemsCenter width100'>
                    {/* <img src={noAccessIcon} alt="No Access" className='icon-size-min' /> */}
                    <div className='flexCol justifyContentCenter alignItemsCenter width100 mt-1'>
                        <span className='fontMedium'>{t('noAccessForThisPage')}</span>
                    </div>
                </div>
            </div>
        }

        {showDownload &&
            <div className="overlay" role="dialog">
                <div className="dialog sm-scrollbar">
                    <div className="dialog__content">
                        <button
                            type="button"
                            className="modal__close_icon"
                            onClick={handleCloseDownloadReport}
                            aria-label="Close"
                            tabIndex={0}
                        >
                            <img src={closeIcon} alt="Close" />
                        </button>

                        <h2 className="dialog__title subheading-text-medium fontMedium">{t('pages.logsContent.downloadDeviceLogs')}</h2>
                        <hr />

                        <div className="dialog__body_container flexRow mt-1 button-align justifyContentCenter flexWrap">
                            <div className={`dialog__container_item input-container`}>
                                <div className={`dialog__description`}>
                                    
                                    <div className='flexCol justifyContentSpaceBetween'>
                                        
                                        <div className='flexCol justifyContentCenter'>
                                            <label className="textAlignLeft fontRegular req" htmlFor="startDate">{t('startDate')}</label>
                                            <NGODatePicker
                                                value={reportData.startDate || ''}
                                                name='startDate'
                                                onChange={ onReportDateChange }
                                                placeholder={t('startDate')}
                                                placeholdertext={t('datePickerPlaceholder')}
                                                minDate={reportData.endDate ? 
                                                    new Date(new Date(reportData.endDate).getTime() - MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000) 
                                                    : null
                                                }
                                                maxDate={reportData.endDate ? new Date(reportData.endDate) : new Date()}
                                                isclearable={ "true" }
                                                tabIndex={1}
                                            />
                                        </div>
                                        <div className='flexCol justifyContentCenter m-2'>
                                            <label className="textAlignLeft fontRegular req" htmlFor="endDate">{t('endDate')}</label>
                                            <NGODatePicker
                                                value={reportData.endDate || ''}
                                                name='endDate'
                                                onChange={ onReportDateChange }
                                                placeholder={t('endDate')}
                                                placeholdertext={t('datePickerPlaceholder')}
                                                minDate={reportData.startDate ? new Date(reportData.startDate) : null}
                                                maxDate={ getEndDateMaxDate(reportData.startDate) }
                                                isclearable={ "true" }
                                                tabIndex={2}
                                            />
                                        </div>
                                        <div className='flexCol justifyContentCenter m-2'>
                                            <label className="textAlignLeft fontRegular req pb-2" htmlFor="reportDeviceId">{t('deviceId')}</label>
                                            <div className="operations-in">
                                                <input
                                                    type="search"
                                                    name='reportDeviceId'
                                                    className="search-crew searchReconFilter req"
                                                    value={reportData.reportDeviceId || ''}
                                                    onChange={ onReportDeviceIdChange }
                                                    placeholder={t('deviceId')}
                                                    tabIndex={3}
                                                />
                                            </div>
                                        </div>

                                        {state.isDownloadReportError ? 
                                            <div className='flex justifyContentCenter alignItemsCenter m-2'>
                                                <div className={ "dialog__statusBlock " + (state.isDownloadReportError ? 'alert_error' : 'alert_success') }>
                                                    {state.message?.text}
                                                </div>
                                            </div>
                                        : null }
                                        
                                        <div className='flexRow justifyContentCenter alignItemsCenter m-2'>
                                            <button type="button"
                                                onClick={ (e) => onDownloadDeviceLogsReport(e) } 
                                                disabled={ isDownloading || !reportData.startDate || !reportData.endDate || !reportData.reportDeviceId } 
                                                className="btn btn-info mt-4" style={{ color: "white", width: "100%" }}>
                                                { isDownloading ? 
                                                    <div className='flexRow justifyContentCenter alignItemsCenter'>
                                                        <img src={LoadingGIF} className='icon-size-min' alt="Loading" /> Processing...
                                                    </div>
                                                    :
                                                    <span><span><img className="icon-size mt-1" alt='downloadMaster' src={downloadMasterIcon} /></span> {t('filters.download')}</span>
                                                }
                                            </button>
                                        </div>

                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        }
        {
            !ready &&<div className='overlay_hide sub'></div>
        }

        {showDetailsModal && detailsData &&
            <div className="overlay" role="dialog">
                <div className={`${ sideBarHidden ? 'dialog-fs2' : 'dialog'} sm-scrollbar`}>
                    <div className="dialog__content ng-grid-right">
                        <div className='dialog-header flexRow alignItemsCenter width100 content-tabs'>
                            <h2 className="dialog__title subheading-text-medium fontMedium justifyContentCenter alignItemsCenter margBottom0 width50">
                                { detailsData.eventCode ? 
                                    detailsData.eventCode + ' : ' + (eventCodeMap[detailsData.eventCode]?.title || detailsData.eventCode) : 
                                'Log Details'
                                }
                            </h2>
                            <button className='close-btn-ht' onClick={() => handleCloseDetailsModal()}>
                            <img src={closeIcon} alt="close" data-icon='close'/>
                            </button>
                        </div>
                        <hr />
                        <div className="dialog__body_container flexCol mt-4">
                            <div className="log-details">
                                <p><strong>Timestamp:</strong> {detailsData.timestamp ? moment(detailsData.timestamp).format(DATETIME_FORMAT) : 'N/A'}</p>
                                <p><strong>Event Code:</strong> 
                                    &nbsp; {detailsData.eventCode || 'N/A'} &nbsp;&nbsp;
                                    <span className="copy p-2 m-2 pointer" title="Copy">
                                        <MdContentCopy alt="Copy" onClick={(e) => handleCopyLogMessage(e.currentTarget, detailsData.eventCode)} />
                                    </span> 
                                </p>
                                <p><strong>Title:</strong> {detailsData.eventCode ? eventCodeMap[detailsData.eventCode]?.title || detailsData.eventCode : 'N/A'}</p>
                                <p>
                                    <strong>Message:</strong>&nbsp;
                                    <span className="copy p-2 m-2 pointer" title="Copy">
                                        <MdContentCopy alt="Copy" onClick={(e) => handleCopyLogMessage(e.currentTarget, detailsData?.message)} />
                                    </span>
                                </p>
                                <p className='dialog-log-message raw-data sm-scrollbar'>
                                    {detailsData.message ? decodeLogMessage(detailsData.message) : 'N/A'}
                                </p>
                                <p>
                                    <strong>Message Parsed:</strong>&nbsp;
                                    <span className="copy p-2 m-2 pointer" title="Copy">
                                        <MdContentCopy alt="Copy" onClick={(e) => handleCopyLogMessage(e.currentTarget, decodeLogMessage(detailsData?.message, '\n'))} />
                                    </span>
                                </p>
                                <div className='dialog-log-message parsed-data sm-scrollbar'>
                                    <pre>{ renderMessageWithHighlights({ data: { message: detailsData.message } }, '\n') }</pre>
                                </div>
                            </div>
                        </div>
                        <div className="dialog__footer flexRow justifyContentCenter alignItemsCenter">
                            <button type="button" className="btn btn-secondary" onClick={() => handleCloseDetailsModal()}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        }
        </div>
    );
};